import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
  getFilteredRowModel,
  getPaginationRowModel,
  Row,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { InventoryItem } from '../interfaces/inventory'
import { injectProductSelector } from './Columns'
import { useProductDetailsContext } from '../contexts/ProductDetailsContext'

import { InventoryTableHeader } from './TableHeader'
import { useTableContext } from './TableContext'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
}

const INVENTORY_TABLE_COLUMNS_STORAGE_KEY = 'inventory-table-columns'

export function DataTable<TData, TValue>({
  columns,
}: DataTableProps<TData, TValue>) {
  const navigate = useNavigate()

  // Use the table context
  const {
    inventoryData,
    isLoading,
    skeletonRowCount,
    isError,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    totalItems,
    totalPages,
  } = useTableContext()

  // Use the product details context for selection
  const { setSelectedProductId } = useProductDetailsContext()

  // Inject the product selector into the columns
  useEffect(() => {
    injectProductSelector(setSelectedProductId)
  }, [setSelectedProductId])

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

  // Create skeleton columns when loading
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _skeletonColumns = useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      // Different width based on column type
      const width = '80%'
      const height = '20px'

      return {
        ...column,
        cell: () => (
          <Skeleton
            className={`h-[${height}] w-[${width}] rounded`}
            style={{ width, height }}
          />
        ),
      }
    })
  }, [columns, isLoading])

  // Load saved column visibility
  useEffect(() => {
    const saved = localStorage.getItem(INVENTORY_TABLE_COLUMNS_STORAGE_KEY)
    if (saved) {
      setColumnVisibility(JSON.parse(saved))
    }
  }, [])

  // Save column visibility changes
  const handleColumnVisibilityChange = (
    updater: VisibilityState | ((_old: VisibilityState) => VisibilityState)
  ) => {
    const newVisibility =
      typeof updater === 'function' ? updater(columnVisibility) : updater
    setColumnVisibility(newVisibility)
    localStorage.setItem(
      INVENTORY_TABLE_COLUMNS_STORAGE_KEY,
      JSON.stringify(newVisibility)
    )
  }

  const tableData = isLoading
    ? (Array.from({ length: skeletonRowCount }, (_, index) => ({
        id: `skeleton-${index}`,
        productId: index,
        productName: '',
        productImageUrl: '',
        quantity: { entryQuantity: 0, availableQuantity: 0 },
        containers: [],
        suppliers: [],
        warehouses: [],
        expirations: [],
        // Compatibility fields
        name: '',
        totalQuantity: 0,
        containersCount: 0,
        suppliersNames: [],
        expirationDate: '',
        lotCount: 0,
        createdAt: '',
        updatedAt: '',
      })) as InventoryItem[])
    : inventoryData

  const table = useReactTable<InventoryItem>({
    data: tableData || [],
    columns: columns as ColumnDef<InventoryItem>[],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    manualPagination: true, // Paginación manejada por el backend
    manualFiltering: true, // Filtrado manejado por el backend
    pageCount: -1, // No conocemos el total de páginas desde el backend
    state: {
      columnVisibility,
      pagination: {
        pageIndex: currentPage - 1, // React Table usa índice base 0
        pageSize: pageSize,
      },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({
          pageIndex: currentPage - 1,
          pageSize: pageSize,
        })
        setCurrentPage(newPagination.pageIndex + 1) // Convertir a base 1 para el backend
        setPageSize(newPagination.pageSize)
      }
    },
  })

  const handleRowClick = (row: Row<unknown>) => {
    if (
      isLoading ||
      !(row.original as { id?: string }).id ||
      (row.original as { id?: string }).id?.startsWith('skeleton-')
    )
      return

    // Navegar a la vista de lotes para este item de inventario
    navigate(`/logistic/inventory/${(row.original as { id: string }).id}/lotes`)
  }

  if (isError) {
    return (
      <div className="flex h-32 items-center justify-center text-gray-500">
        Error al cargar los datos del inventario
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* New Table Header with filters and search */}
      <InventoryTableHeader
        onSearchChange={setSearchQuery}
        searchValue={searchQuery}
        table={table}
      />

      <div className="overflow-hidden rounded-lg border bg-white">
        <div className="relative">
          {/* Header fijo con posición absoluta */}
          <div className="absolute left-0 right-0 top-0 z-10 bg-white">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    className="border-0"
                    key={headerGroup.id}
                    style={{ borderBottom: 'none' }}
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        className="border-0 bg-white px-4 py-3 text-left font-medium text-gray-900"
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          borderBottom: 'none',
                          borderTop: 'none',
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
          </div>

          {/* Contenido scrolleable con padding-top para el header */}
          <div
            className="overflow-auto bg-white"
            style={{
              paddingTop: '49px',
              height: 'calc(100vh - 280px)',
              minHeight: '450px',
              maxHeight: 'calc(100vh - 280px)',
            }}
          >
            <Table>
              <TableBody className="bg-white">
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row, index) => (
                    <TableRow
                      className={`h-12 cursor-pointer bg-white transition-colors hover:bg-muted/50 ${
                        isLoading ? 'animate-pulse' : ''
                      } ${index === 0 ? 'border-t' : ''}`}
                      data-state={row.getIsSelected() && 'selected'}
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          className="bg-white px-4 py-2"
                          key={cell.id}
                          style={{ width: cell.column.getSize() }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow className="border-t">
                    <TableCell
                      className="h-24 bg-white text-center"
                      colSpan={columns.length}
                    >
                      {isLoading
                        ? 'Cargando...'
                        : 'No hay datos de inventario.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Footer with pagination controls */}
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="text-sm text-black">
          {isLoading
            ? 'Cargando...'
            : `Mostrando ${(currentPage - 1) * pageSize + 1}-${Math.min(
                currentPage * pageSize,
                totalItems
              )} de ${totalItems} resultados`}
        </div>

        <div className="flex items-center space-x-1">
          {/* Botón anterior */}
          <Button
            className="h-8 w-8 border-none bg-transparent p-0 text-[#262626] hover:bg-transparent hover:text-[#262626]"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            size="sm"
            variant="ghost"
          >
            <span className="sr-only">Ir a la página anterior</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Números de página */}
          {(() => {
            const pages = []
            const showEllipsis = totalPages > 7

            if (!showEllipsis) {
              // Si hay 7 páginas o menos, mostrar todas
              for (let i = 1; i <= totalPages; i++) {
                pages.push(
                  <Button
                    className={`h-8 w-8 border-none p-0 ${
                      currentPage === i
                        ? 'bg-[#FAFAFA] text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626]'
                        : 'bg-transparent text-[#262626] hover:bg-transparent hover:text-[#262626]'
                    }`}
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    size="sm"
                    variant="ghost"
                  >
                    {i}
                  </Button>
                )
              }
            } else {
              // Lógica para más de 7 páginas
              // Siempre mostrar página 1
              pages.push(
                <Button
                  className={`h-8 w-8 border-none p-0 ${
                    currentPage === 1
                      ? 'bg-[#FAFAFA] text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626]'
                      : 'bg-transparent text-[#262626] hover:bg-transparent hover:text-[#262626]'
                  }`}
                  key={1}
                  onClick={() => setCurrentPage(1)}
                  size="sm"
                  variant="ghost"
                >
                  1
                </Button>
              )

              // Determinar rango de páginas a mostrar alrededor de la actual
              let startPage = Math.max(2, currentPage - 1)
              let endPage = Math.min(totalPages - 1, currentPage + 1)

              // Ajustar el rango si estamos cerca del inicio o final
              if (currentPage <= 3) {
                endPage = Math.min(5, totalPages - 1)
              } else if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 4)
              }

              // Mostrar ellipsis al inicio si es necesario
              if (startPage > 2) {
                pages.push(
                  <span
                    className="flex h-8 w-8 items-center justify-center text-sm text-[#262626]"
                    key="ellipsis-start"
                  >
                    ...
                  </span>
                )
              }

              // Mostrar páginas del rango
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <Button
                    className={`h-8 w-8 border-none p-0 ${
                      currentPage === i
                        ? 'bg-[#FAFAFA] text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626]'
                        : 'bg-transparent text-[#262626] hover:bg-transparent hover:text-[#262626]'
                    }`}
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    size="sm"
                    variant="ghost"
                  >
                    {i}
                  </Button>
                )
              }

              // Mostrar ellipsis al final si es necesario
              if (endPage < totalPages - 1) {
                pages.push(
                  <span
                    className="flex h-8 w-8 items-center justify-center text-sm text-[#262626]"
                    key="ellipsis-end"
                  >
                    ...
                  </span>
                )
              }

              // Siempre mostrar la última página
              if (totalPages > 1) {
                pages.push(
                  <Button
                    className={`h-8 w-8 border-none p-0 ${
                      currentPage === totalPages
                        ? 'bg-[#FAFAFA] text-[#262626] hover:bg-[#FAFAFA] hover:text-[#262626]'
                        : 'bg-transparent text-[#262626] hover:bg-transparent hover:text-[#262626]'
                    }`}
                    key={totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    size="sm"
                    variant="ghost"
                  >
                    {totalPages}
                  </Button>
                )
              }
            }

            return pages
          })()}

          {/* Botón siguiente */}
          <Button
            className="h-8 w-8 border-none bg-transparent p-0 text-[#262626] hover:bg-transparent hover:text-[#262626]"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            size="sm"
            variant="ghost"
          >
            <span className="sr-only">Ir a la página siguiente</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
